import { useQuery } from '@tanstack/react-query';
import axiosInstance from 'utils/axiosInterceptors';

export const AWAITING_COUNTS_KEY = ['awaiting-counts'] as const;

export type AwaitingCountKey =
  | 'procurementInvoicesForApproval'
  | 'purchaseOrdersAwaiting'
  | 'supplierPaymentsAwaiting'
  | 'actualQuantityCountsPending';

export type AwaitingProcessingCounts = {
  procurementInvoicesForApproval: number;
  purchaseOrdersAwaiting: number;
  supplierPaymentsAwaiting: number;
  actualQuantityCountsPending: number;
};

const EMPTY_COUNTS: AwaitingProcessingCounts = {
  procurementInvoicesForApproval: 0,
  purchaseOrdersAwaiting: 0,
  supplierPaymentsAwaiting: 0,
  actualQuantityCountsPending: 0
};

const parseCount = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const mapAwaitingCounts = (data: unknown): AwaitingProcessingCounts => {
  const payload =
    data != null &&
    typeof data === 'object' &&
    'data' in data &&
    typeof (data as { data?: unknown }).data === 'object' &&
    (data as { data?: unknown }).data != null
      ? ((data as { data: Record<string, unknown> }).data as Record<
          string,
          unknown
        >)
      : ((data ?? {}) as Record<string, unknown>);

  return {
    procurementInvoicesForApproval: parseCount(
      payload.procurementInvoicesForApproval ??
        payload.ProcurementInvoicesForApproval
    ),
    purchaseOrdersAwaiting: parseCount(
      payload.purchaseOrdersAwaiting ?? payload.PurchaseOrdersAwaiting
    ),
    supplierPaymentsAwaiting: parseCount(
      payload.supplierPaymentsAwaiting ?? payload.SupplierPaymentsAwaiting
    ),
    actualQuantityCountsPending: parseCount(
      payload.actualQuantityCountsPending ?? payload.ActualQuantityCountsPending
    )
  };
};

const fetchAwaitingCounts = async (
  branchDomain: string
): Promise<AwaitingProcessingCounts> => {
  const { data } = await axiosInstance.get(
    `/api/v1/${branchDomain}/awaiting-counts`
  );
  return mapAwaitingCounts(data);
};

export const useAwaitingProcessingCounts = (branchDomain?: string | null) =>
  useQuery({
    queryKey: [...AWAITING_COUNTS_KEY, branchDomain],
    queryFn: () => fetchAwaitingCounts(branchDomain as string),
    enabled: Boolean(branchDomain),
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    placeholderData: EMPTY_COUNTS
  });
